import React, { useState, useEffect } from 'react';
import { apiGet, apiPost, apiPut, apiDelete, apiUpload, API_ENDPOINTS, getFullImageUrl } from '../utils/api';
import toast from 'react-hot-toast';
import { 
  X, 
  Upload, 
  Link as LinkIcon, 
  ArrowUp, 
  ArrowDown, 
  Star, 
  Trash2, 
  Edit2, 
  Check, 
  Image as ImageIcon,
  Loader2,
  Eye,
  Move
} from 'lucide-react';

const ImageManagerModal = ({ isOpen, onClose, item, type = 'product', onUpdate }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('upload'); // 'upload' | 'url'
  const [urlInput, setUrlInput] = useState('');
  const [captionInput, setCaptionInput] = useState('');
  const [altTextInput, setAltTextInput] = useState('');
  const [editingImageId, setEditingImageId] = useState(null);
  const [editCaption, setEditCaption] = useState('');
  const [editAltText, setEditAltText] = useState('');
  const [previewZoomUrl, setPreviewZoomUrl] = useState(null);
  const [savingRankings, setSavingRankings] = useState(false);

  const isProduct = type === 'product';
  const title = isProduct ? 'Product Images & Ranking' : 'Service Images & Ranking';

  useEffect(() => {
    if (isOpen && item?.id) {
      fetchImages();
    }
  }, [isOpen, item?.id]);

  const fetchImages = async () => {
    if (!item?.id) return;
    setLoading(true);
    try {
      const endpoint = isProduct 
        ? API_ENDPOINTS.PRODUCT_IMAGES(item.id) 
        : API_ENDPOINTS.SERVICE_IMAGES(item.id);
      const res = await apiGet(endpoint);
      const list = Array.isArray(res) ? res : (res?.data || []);
      // Sort by ranking ascending
      list.sort((a, b) => (a.ranking || 0) - (b.ranking || 0));
      setImages(list);
    } catch (err) {
      console.error('Error fetching images:', err);
      // Fallback to item.images if available
      if (item.images && Array.isArray(item.images)) {
        const sorted = [...item.images].sort((a, b) => (a.ranking || 0) - (b.ranking || 0));
        setImages(sorted);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !item) return null;

  // Handle single or multiple file upload
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setUploading(true);
    try {
      const newImagesList = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        // 1. Upload file to server
        const uploadRes = await apiUpload(API_ENDPOINTS.UPLOAD_IMAGE, file);
        const fileUrl = uploadRes?.data?.url || uploadRes?.url;

        if (!fileUrl) {
          throw new Error('Failed to retrieve uploaded image URL');
        }

        // 2. Add image to product/service
        const nextRank = images.length + newImagesList.length + 1;
        const addEndpoint = isProduct 
          ? API_ENDPOINTS.PRODUCT_IMAGES(item.id) 
          : API_ENDPOINTS.SERVICE_IMAGES(item.id);

        const addRes = await apiPost(addEndpoint, {
          image_url: fileUrl,
          ranking: nextRank,
          caption: captionInput.trim() || file.name,
          alt_text: altTextInput.trim() || item.name,
          is_primary: images.length === 0 && newImagesList.length === 0
        });

        const addedList = Array.isArray(addRes) ? addRes : (addRes?.data || []);
        if (addedList.length > 0) {
          newImagesList.push(...addedList);
        }
      }

      toast.success(`${files.length} image(s) uploaded successfully!`);
      setUrlInput('');
      setCaptionInput('');
      setAltTextInput('');
      await fetchImages();
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Upload error:', err);
      toast.error(err?.error?.message || err?.message || 'Failed to upload image');
    } finally {
      setUploading(false);
      // Reset file input
      if (e.target) e.target.value = '';
    }
  };

  // Handle adding image via URL
  const handleAddUrlImage = async (e) => {
    e.preventDefault();
    if (!urlInput.trim()) {
      toast.error('Please enter a valid image URL');
      return;
    }

    setUploading(true);
    try {
      const nextRank = images.length + 1;
      const addEndpoint = isProduct 
        ? API_ENDPOINTS.PRODUCT_IMAGES(item.id) 
        : API_ENDPOINTS.SERVICE_IMAGES(item.id);

      await apiPost(addEndpoint, {
        image_url: urlInput.trim(),
        ranking: nextRank,
        caption: captionInput.trim() || null,
        alt_text: altTextInput.trim() || item.name,
        is_primary: images.length === 0
      });

      toast.success('Image added successfully!');
      setUrlInput('');
      setCaptionInput('');
      setAltTextInput('');
      await fetchImages();
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Error adding image URL:', err);
      toast.error(err?.error?.message || err?.message || 'Failed to add image');
    } finally {
      setUploading(false);
    }
  };

  // Move image up in rank (swap with previous)
  const handleMoveUp = async (index) => {
    if (index <= 0) return;
    const newImages = [...images];
    const current = newImages[index];
    const above = newImages[index - 1];

    // Swap positions
    newImages[index - 1] = current;
    newImages[index] = above;

    // Recalculate rankings (1-indexed)
    const rankings = newImages.map((img, i) => ({
      id: img.id,
      ranking: i + 1
    }));

    setImages(newImages.map((img, i) => ({ ...img, ranking: i + 1, is_primary: i === 0 })));
    await saveRankings(rankings);
  };

  // Move image down in rank (swap with next)
  const handleMoveDown = async (index) => {
    if (index >= images.length - 1) return;
    const newImages = [...images];
    const current = newImages[index];
    const below = newImages[index + 1];

    // Swap positions
    newImages[index + 1] = current;
    newImages[index] = below;

    const rankings = newImages.map((img, i) => ({
      id: img.id,
      ranking: i + 1
    }));

    setImages(newImages.map((img, i) => ({ ...img, ranking: i + 1, is_primary: i === 0 })));
    await saveRankings(rankings);
  };

  // Set specific image as Cover / Primary (Rank #1)
  const handleSetPrimary = async (imgId) => {
    const targetIdx = images.findIndex(img => img.id === imgId);
    if (targetIdx <= 0) return; // Already primary

    const newImages = [...images];
    const [target] = newImages.splice(targetIdx, 1);
    newImages.unshift(target);

    const rankings = newImages.map((img, i) => ({
      id: img.id,
      ranking: i + 1
    }));

    setImages(newImages.map((img, i) => ({ ...img, ranking: i + 1, is_primary: i === 0 })));
    await saveRankings(rankings);
    toast.success('Cover image updated! Set as #1 rank.');
  };

  // Direct manual rank input change
  const handleManualRankChange = async (imgId, targetRank) => {
    const newRank = parseInt(targetRank, 10);
    if (isNaN(newRank) || newRank < 1) return;

    const targetIdx = images.findIndex(img => img.id === imgId);
    if (targetIdx === -1) return;

    const newImages = [...images];
    const [moved] = newImages.splice(targetIdx, 1);

    // Insert at clamped position (0-indexed: targetRank - 1)
    const insertIdx = Math.max(0, Math.min(newRank - 1, newImages.length));
    newImages.splice(insertIdx, 0, moved);

    const rankings = newImages.map((img, i) => ({
      id: img.id,
      ranking: i + 1
    }));

    setImages(newImages.map((img, i) => ({ ...img, ranking: i + 1, is_primary: i === 0 })));
    await saveRankings(rankings);
  };

  // Persist rankings to backend
  const saveRankings = async (rankings) => {
    setSavingRankings(true);
    try {
      const endpoint = isProduct 
        ? API_ENDPOINTS.PRODUCT_IMAGES_RANKING(item.id) 
        : API_ENDPOINTS.SERVICE_IMAGES_RANKING(item.id);
      
      const res = await apiPut(endpoint, { rankings });
      const updatedList = Array.isArray(res) ? res : (res?.data || []);
      if (updatedList.length > 0) {
        setImages(updatedList);
      }
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Failed to save rankings:', err);
      toast.error('Failed to update ranking on server');
    } finally {
      setSavingRankings(false);
    }
  };

  // Update caption or alt text
  const handleSaveImageDetails = async (imgId) => {
    try {
      const endpoint = isProduct 
        ? API_ENDPOINTS.PRODUCT_IMAGE_DETAIL(imgId) 
        : API_ENDPOINTS.SERVICE_IMAGE_DETAIL(imgId);

      await apiPut(endpoint, {
        caption: editCaption,
        alt_text: editAltText
      });

      toast.success('Image details updated');
      setEditingImageId(null);
      await fetchImages();
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Update image error:', err);
      toast.error('Failed to update image details');
    }
  };

  // Delete image
  const handleDeleteImage = async (imgId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;

    try {
      const endpoint = isProduct 
        ? API_ENDPOINTS.PRODUCT_IMAGE_DETAIL(imgId) 
        : API_ENDPOINTS.SERVICE_IMAGE_DETAIL(imgId);

      await apiDelete(endpoint);
      toast.success('Image deleted');
      await fetchImages();
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Delete image error:', err);
      toast.error('Failed to delete image');
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[92vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/50">
          <div>
            <div className="flex items-center gap-2">
              <span className="p-1.5 bg-[#1f1f1f] text-white rounded-lg text-xs font-semibold uppercase tracking-wider">
                {type}
              </span>
              <h2 className="text-xl font-bold text-gray-900">{title}</h2>
            </div>
            <p className="text-sm text-gray-500 mt-0.5">
              Managing gallery for: <span className="font-semibold text-gray-800">{item.name}</span>
              {item.sku ? <span className="ml-2 text-xs font-mono bg-gray-200 px-1.5 py-0.5 rounded">SKU: {item.sku}</span> : null}
            </p>
          </div>
          <button 
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          
          {/* Add Image Section */}
          <div className="bg-gradient-to-r from-gray-50 to-slate-50 border border-gray-200/80 rounded-xl p-5 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold uppercase tracking-wider text-gray-700 flex items-center gap-2">
                <Upload className="w-4 h-4 text-gray-600" />
                Add New Image
              </h3>
              
              {/* Tab Selector */}
              <div className="flex bg-gray-200/80 p-0.5 rounded-lg text-xs font-medium">
                <button
                  type="button"
                  onClick={() => setActiveTab('upload')}
                  className={`px-3 py-1.5 rounded-md transition-all ${
                    activeTab === 'upload' 
                      ? 'bg-white text-gray-900 shadow-sm font-semibold' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Upload File
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('url')}
                  className={`px-3 py-1.5 rounded-md transition-all ${
                    activeTab === 'url' 
                      ? 'bg-white text-gray-900 shadow-sm font-semibold' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Image URL
                </button>
              </div>
            </div>

            {/* Upload File Tab */}
            {activeTab === 'upload' ? (
              <div className="space-y-3">
                <label className="border-2 border-dashed border-gray-300 hover:border-gray-500 rounded-xl p-6 flex flex-col items-center justify-center cursor-pointer transition-colors bg-white/70 hover:bg-white group">
                  <div className="p-3 bg-gray-100 group-hover:bg-gray-200 rounded-full mb-2 transition-colors">
                    {uploading ? (
                      <Loader2 className="w-6 h-6 animate-spin text-gray-800" />
                    ) : (
                      <Upload className="w-6 h-6 text-gray-600 group-hover:text-gray-900" />
                    )}
                  </div>
                  <span className="text-sm font-semibold text-gray-800">
                    {uploading ? 'Uploading...' : 'Click to upload or drag & drop'}
                  </span>
                  <span className="text-xs text-gray-500 mt-1">
                    Supports PNG, JPG, WEBP, GIF, SVG (up to 10MB per file)
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    disabled={uploading}
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </label>
              </div>
            ) : (
              /* Image URL Tab */
              <form onSubmit={handleAddUrlImage} className="space-y-3">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <LinkIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="url"
                      placeholder="https://example.com/image.jpg"
                      value={urlInput}
                      onChange={(e) => setUrlInput(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1f1f1f] focus:outline-none"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={uploading || !urlInput.trim()}
                    className="px-4 py-2 bg-[#1f1f1f] text-white text-sm font-medium rounded-lg hover:bg-black disabled:opacity-50 transition-colors flex items-center gap-1.5"
                  >
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Image'}
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    placeholder="Optional caption / label"
                    value={captionInput}
                    onChange={(e) => setCaptionInput(e.target.value)}
                    className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-400"
                  />
                  <input
                    type="text"
                    placeholder="Optional alt text"
                    value={altTextInput}
                    onChange={(e) => setAltTextInput(e.target.value)}
                    className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-400"
                  />
                </div>
              </form>
            )}
          </div>

          {/* Image Ranking & Gallery List */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-bold uppercase tracking-wider text-gray-800">
                  Current Images ({images.length})
                </h3>
                {savingRankings && (
                  <span className="text-xs text-amber-600 flex items-center gap-1 animate-pulse">
                    <Loader2 className="w-3 h-3 animate-spin" /> Saving order...
                  </span>
                )}
              </div>
              <span className="text-xs text-gray-500">
                ⭐ Rank #1 is the primary cover image shown everywhere.
              </span>
            </div>

            {loading ? (
              <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                <Loader2 className="w-8 h-8 animate-spin mb-2 text-[#1f1f1f]" />
                <p className="text-sm">Loading gallery images...</p>
              </div>
            ) : images.length === 0 ? (
              <div className="border border-dashed border-gray-300 rounded-xl p-12 text-center bg-gray-50/50">
                <ImageIcon className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                <h4 className="text-base font-semibold text-gray-700">No images added yet</h4>
                <p className="text-xs text-gray-500 max-w-sm mx-auto mt-1">
                  Upload photos or add image links above. You can add multiple images and adjust their display ranking.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {images.map((img, index) => {
                  const isPrimary = index === 0 || img.is_primary || img.ranking === 1;
                  const isEditing = editingImageId === img.id;
                  const fullUrl = getFullImageUrl(img.image_url);

                  return (
                    <div 
                      key={img.id}
                      className={`flex flex-col sm:flex-row sm:items-center justify-between p-3.5 rounded-xl border transition-all duration-150 gap-4 ${
                        isPrimary 
                          ? 'border-amber-300 bg-amber-50/30 shadow-sm ring-1 ring-amber-300/60' 
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    >
                      {/* Left: Thumbnail & Rank Badge & Info */}
                      <div className="flex items-center gap-4 min-w-0 flex-1">
                        
                        {/* Rank Badge */}
                        <div className="flex flex-col items-center justify-center min-w-[52px]">
                          <span 
                            className={`px-2 py-0.5 rounded-full text-xs font-bold tracking-tight flex items-center gap-1 ${
                              isPrimary
                                ? 'bg-amber-500 text-white shadow-sm'
                                : 'bg-gray-100 text-gray-700 border border-gray-200'
                            }`}
                          >
                            {isPrimary && <Star className="w-3 h-3 fill-current" />}
                            #{index + 1}
                          </span>
                          <span className="text-[10px] text-gray-400 mt-0.5">
                            {isPrimary ? 'Cover' : 'Rank'}
                          </span>
                        </div>

                        {/* Thumbnail */}
                        <div 
                          className="relative w-20 h-20 sm:w-24 sm:h-24 rounded-lg overflow-hidden border border-gray-200 bg-gray-100 flex-shrink-0 cursor-pointer group"
                          onClick={() => setPreviewZoomUrl(fullUrl)}
                          title="Click to preview full size"
                        >
                          <img
                            src={fullUrl}
                            alt={img.alt_text || img.caption || item.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'https://placehold.co/100x100?text=No+Preview';
                            }}
                          />
                          <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity text-white">
                            <Eye className="w-5 h-5" />
                          </div>
                        </div>

                        {/* Metadata / Details */}
                        <div className="min-w-0 flex-1 space-y-1">
                          {isEditing ? (
                            <div className="space-y-1.5 py-1">
                              <input
                                type="text"
                                placeholder="Caption"
                                value={editCaption}
                                onChange={(e) => setEditCaption(e.target.value)}
                                className="w-full text-xs px-2.5 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-black focus:outline-none"
                              />
                              <input
                                type="text"
                                placeholder="Alt text"
                                value={editAltText}
                                onChange={(e) => setEditAltText(e.target.value)}
                                className="w-full text-xs px-2.5 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-black focus:outline-none"
                              />
                              <div className="flex gap-2 pt-1">
                                <button
                                  type="button"
                                  onClick={() => handleSaveImageDetails(img.id)}
                                  className="px-2.5 py-1 bg-green-600 text-white rounded text-xs font-medium hover:bg-green-700 flex items-center gap-1"
                                >
                                  <Check className="w-3 h-3" /> Save
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setEditingImageId(null)}
                                  className="px-2.5 py-1 bg-gray-200 text-gray-700 rounded text-xs hover:bg-gray-300"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-semibold text-gray-900 truncate">
                                  {img.caption || `Image ${index + 1}`}
                                </p>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setEditingImageId(img.id);
                                    setEditCaption(img.caption || '');
                                    setEditAltText(img.alt_text || '');
                                  }}
                                  className="text-gray-400 hover:text-gray-700 p-1"
                                  title="Edit caption & alt text"
                                >
                                  <Edit2 className="w-3.5 h-3.5" />
                                </button>
                              </div>

                              {img.alt_text && (
                                <p className="text-xs text-gray-500 truncate">
                                  Alt: {img.alt_text}
                                </p>
                              )}

                              <p className="text-[11px] text-gray-400 font-mono truncate max-w-md">
                                {img.image_url}
                              </p>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Right: Ranking & Action Controls */}
                      <div className="flex items-center justify-between sm:justify-end gap-2 border-t sm:border-t-0 pt-2 sm:pt-0">
                        
                        {/* Up / Down Reorder Buttons */}
                        <div className="flex items-center bg-gray-100 rounded-lg p-0.5 border border-gray-200">
                          <button
                            type="button"
                            disabled={index === 0 || savingRankings}
                            onClick={() => handleMoveUp(index)}
                            title="Move Up in Rank"
                            className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-white rounded disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                          >
                            <ArrowUp className="w-4 h-4" />
                          </button>
                          <span className="text-xs font-mono font-medium text-gray-600 px-1.5">
                            {index + 1}
                          </span>
                          <button
                            type="button"
                            disabled={index === images.length - 1 || savingRankings}
                            onClick={() => handleMoveDown(index)}
                            title="Move Down in Rank"
                            className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-white rounded disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                          >
                            <ArrowDown className="w-4 h-4" />
                          </button>
                        </div>

                        {/* Direct Rank Selector */}
                        <div className="flex items-center gap-1">
                          <label className="text-[11px] text-gray-500 sr-only">Rank</label>
                          <select
                            value={index + 1}
                            onChange={(e) => handleManualRankChange(img.id, e.target.value)}
                            disabled={savingRankings}
                            className="text-xs border border-gray-300 rounded-lg px-2 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-black"
                            title="Set ranking directly"
                          >
                            {images.map((_, rIdx) => (
                              <option key={rIdx + 1} value={rIdx + 1}>
                                Rank #{rIdx + 1}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Make Cover Button */}
                        {!isPrimary && (
                          <button
                            type="button"
                            onClick={() => handleSetPrimary(img.id)}
                            disabled={savingRankings}
                            className="px-2.5 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-lg transition-colors flex items-center gap-1"
                            title="Make this the #1 Cover Image"
                          >
                            <Star className="w-3.5 h-3.5" />
                            <span className="hidden sm:inline">Set Cover</span>
                          </button>
                        )}

                        {/* Delete Button */}
                        <button
                          type="button"
                          onClick={() => handleDeleteImage(img.id)}
                          className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors ml-1"
                          title="Delete image"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-3.5 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
          <div className="text-xs text-gray-500">
            {images.length > 0 ? (
              <span>
                Images automatically save in ranked display order. Rank #1 is used as default preview.
              </span>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2 bg-[#1f1f1f] text-white text-sm font-semibold rounded-lg hover:bg-black transition-colors"
          >
            Done
          </button>
        </div>

      </div>

      {/* Image Full Size Zoom Modal */}
      {previewZoomUrl && (
        <div 
          className="fixed inset-0 z-60 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setPreviewZoomUrl(null)}
        >
          <div className="relative max-w-3xl max-h-[90vh] bg-black rounded-lg overflow-hidden">
            <button
              onClick={() => setPreviewZoomUrl(null)}
              className="absolute top-3 right-3 p-2 bg-black/60 text-white rounded-full hover:bg-black"
            >
              <X className="w-5 h-5" />
            </button>
            <img 
              src={previewZoomUrl} 
              alt="Zoom Preview" 
              className="max-h-[85vh] w-auto object-contain mx-auto" 
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageManagerModal;
